import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Icon, Image, Popup } from 'semantic-ui-react';
import i18next from 'i18next';

import { DataGrid } from '../../common';
import ReceiptAppendixEditor from './ReceiptAppendixEditor';
import SideEditor from '../../general/SideEditor';
import KioskEditor from './KioskEditor';
import { getKiosksData } from '../../../selectors';
import { setEditedEntity, confirmDelete, fetchBrands, fetchKiosks, saveKiosk, deleteKiosk } from '../../../actions';
import { S3_BASE_URL, S3_COMMON_ASSETS } from '../../../config';
import { hasSuperPermission } from '../../../helpers/permissions';
import api from '../../../actions/api';

class Kiosks extends Component {
    state = { receiptEditMode: false };

    componentDidMount() {
        this.props.fetchKiosks([this.props.restaurantId]);
        this.props.fetchBrands(hasSuperPermission('operation') ? null : this.props.restaurantId);
    }

    toggleKioskStatus(kiosk) {
        this.props.saveKiosk({ id: kiosk.id, isDisabled: !kiosk.isDisabled });
    }

    handleDelete = (kiosk) => {
        this.props.confirmDelete(kiosk.id, () => this.props.deleteKiosk(kiosk.id));
    }

    renderMenuButtons() {
        const { setEditedEntity } = this.props;

        return (
            <div>
                <Button
                    floated="right"
                    primary
                    onClick={() => setEditedEntity('kiosk', { restaurantId: this.props.restaurantId })}
                >
                    {i18next.t('create')}
                </Button>
                <Button
                    floated="right"
                    basic
                    onClick={() => this.setState({ receiptEditMode: true })}
                >
                    {i18next.t('receiptConfig')}
                </Button>
                <Button
                    floated="right"
                    basic
                    onClick={async () => {
                        const { restaurantId } = this.props;
                        const { data } = await api.restaurant.get(restaurantId).receipt.getConfig();
                        var barcode_format = prompt("Enter barcode format", data && data.barcode_format || "{paiditOrderId}");
                        if (barcode_format != null) {
                            await api.restaurant.get(restaurantId).receipt.setConfig({ barcode_format });
                        }
                    }}
                >
                    {i18next.t('barcodeFormat')}
                </Button>
            </div>
        );
    }

    render() {
        const { kiosks, onEdit, setEditedEntity } = this.props;

        if (this.state.receiptEditMode) {
            return <ReceiptAppendixEditor restaurantId={this.props.restaurantId} onClose={() => this.setState({ receiptEditMode: false })} />;
        }

        const columns = [{
            Header: '#',
            headerStyle: { textAlign: 'center' },
            maxWidth: 50,
            accessor: 'id',
            style: { textAlign: 'center' }
        }, {
            Header: i18next.t('kiosks.restaurantName'),
            accessor: 'restaurantName',
            filterable: true
        }, {
            id: 'templateAndTheme',
            Header: i18next.t('kiosks.templateAndTheme'),
            maxWidth: 100,
            accessor: kiosk => `${kiosk.template} - ${kiosk.theme}`,
            filterable: true
        }, {
            Header: i18next.t('general.paymentMethods'),
            filterable: false,
            Cell: row => {
                const kiosk = row.original;

                return (
                    (kiosk.paymentProviders || []).map(provider => {
                        return (
                            <Image
                                key={provider}
                                size="mini"
                                inline
                                spaced="left"
                                src={`${S3_BASE_URL}${S3_COMMON_ASSETS}/${provider}.png`}
                            />
                        );
                    })
                );
            }
        }, {
            Header: i18next.t('supportedOrderTypes'),
            filterable: false,
            Cell: row => {
                const kiosk = row.original;

                return (
                    (kiosk.orderOptions || []).map(option => {
                        return (
                            <Popup
                                content={i18next.t(option)}
                                key={option}
                                trigger={
                                    <Image
                                        key={option}
                                        size="mini"
                                        inline
                                        spaced="left"
                                        src={`${S3_BASE_URL}${S3_COMMON_ASSETS}/${option}.png`}
                                    />
                                }
                            />
                        );
                    })
                );
            }
        }, {
            id: 'loyaltyProgramProvider',
            Header: i18next.t('kiosks.loyaltyProgramProvider'),
            maxWidth: 150,
            accessor: kiosk => kiosk.loyaltyProgramProvider || '---',
            filterable: true
        }, {
            id: 'servingMethod',
            Header: i18next.t('kiosks.servingMethod'),
            maxWidth: 130,
            accessor: kiosk => i18next.t(`kiosks.servingMethods.${kiosk.servingMethod}`),
            filterable: true
        }, {
            Header: i18next.t('note'),
            minWidth: 120,
            accessor: 'note',
            filterable: true
        }, {
            id: 'actions',
            Header: i18next.t('actions'),
            minWidth: 142,
            maxWidth: 142,
            sortable: false,
            filterable: false,
            Cell: row => {
                const kiosk = row.original;

                return (
                    <div>
                        <Button circular size='tiny' icon='write' basic onClick={() => setEditedEntity('kiosk', kiosk)} />
                        <Button circular size='tiny' icon='copy' basic onClick={() => this.props.setEditedEntity('kiosk', { ...kiosk, id: undefined })} />
                        <Button
                            circular
                            size='tiny'
                            icon={kiosk.isDisabled ? 'lock' : 'unlock'}
                            positive={!kiosk.isDisabled}
                            negative={kiosk.isDisabled}
                            onClick={() => this.toggleKioskStatus(kiosk)}
                        />
                        {hasSuperPermission('operation') && <Button circular size='tiny' icon='trash' negative onClick={() => this.handleDelete(kiosk)} />}

                    </div>
                );
            }
        }];

        return (
            <div style={{ padding: 10 }}>
                {<SideEditor
                    title={i18next.t('kiosk')}
                    visible={onEdit}
                    handleClose={() => setEditedEntity(null)}
                >
                    <KioskEditor />
                </SideEditor>}

                <DataGrid
                    data={kiosks}
                    columns={columns}
                    getTrGroupProps={(state, rowInfo, column) => {
                        if (typeof rowInfo !== "undefined") {
                            const kiosk = rowInfo.original;

                            if (kiosk.isDisabled) {
                                return { className: 'pi-data-grid-row-negative' };
                            }

                            return {};
                        }

                        return {};
                    }}
                    extraActions={this.renderMenuButtons()}
                    paginated

                />
            </div>
        );
    }
}

Kiosks.propTypes = {
    fetchBrands: PropTypes.func,
    fetchKiosks: PropTypes.func,
    saveKiosk: PropTypes.func,
    deleteKiosk: PropTypes.func,
    kiosks: PropTypes.array,
    onEdit: PropTypes.bool,
    setEditedEntity: PropTypes.func,
    confirmDelete: PropTypes.func,

    restaurantId: PropTypes.string
};

const mapStateToProps = (state, ownProps) => {
    const restaurantId = ownProps.match.params.restId;
    const onEdit = state.editor.type === 'kiosk';
    const kiosks = getKiosksData(state);

    return { kiosks, onEdit, restaurantId };
};

export default connect(mapStateToProps,
    { fetchBrands, fetchKiosks, saveKiosk, deleteKiosk, setEditedEntity, confirmDelete })(Kiosks);
